import * as Yup from 'yup';
import {
  emailRegexp,
  fullRegexp,
  generalRegexp,
  registerEmailLength,
  UserCompanyAddress,
  UserCompanyName,
  UserCompanyPosition,
  UserCompanyZip,
  UserFirstName,
  UserFullName,
  UserInitials,
  UserLastName,
  UserMiddleName,
} from '@app/const/validation';
import { UserValidation, Warning } from '@app/const/translations';
import { phoneFieldNumberRegexp, ProfileFields } from '@app/const/consts';
import { checkValidityPhoneNumber } from '@app/utils/utils';

export const businessBoxUpdateValidationSchema = Yup.object().shape({
  [ProfileFields.EMAIL]: Yup.string()
    .trim()
    .required(Warning.required)
    .email(Warning.email)
    .matches(emailRegexp, Warning.email)
    .max(registerEmailLength.MAX, Warning.max[registerEmailLength.MAX])
    .nullable(),
  [ProfileFields.COMPANY_NAME]: Yup.string()
    .trim()
    .required(Warning.required)
    .min(UserCompanyName.MIN, Warning.min[UserCompanyName.MIN])
    .max(UserCompanyName.MAX, Warning.max[UserCompanyName.MAX])
    .matches(fullRegexp, Warning.regexp_required)
    .nullable(),
  [ProfileFields.COMPANY_ZIP]: Yup.string()
    .trim()
    .min(UserCompanyZip.MIN, Warning.zipCodeMin)
    .max(UserCompanyZip.MAX, Warning.zipCodeMax)
    .matches(fullRegexp, Warning.regexp_required)
    .nullable(),
  [ProfileFields.COMPANY_ADDRES]: Yup.string()
    .trim()
    .min(UserCompanyAddress.MIN, Warning.min[UserCompanyAddress.MIN])
    .max(UserCompanyAddress.MAX, Warning.max[UserCompanyAddress.MAX])
    .matches(fullRegexp, Warning.regexp_required)
    .nullable(),
});

export const businessBoxCreateValidationSchema =
  businessBoxUpdateValidationSchema.shape({
    [ProfileFields.POSITION]: Yup.string()
      .trim()
      .max(UserCompanyPosition.MAX, Warning.max[UserCompanyPosition.MAX])
      .matches(generalRegexp, UserValidation.regexp)
      .nullable(),
  });

export const inviteUsersValidationSchema = Yup.object()
  .shape({
    [ProfileFields.LAST_NAME]: Yup.string()
      .trim()
      .min(UserLastName.MIN, Warning.min[UserLastName.MIN])
      .max(UserLastName.MAX, Warning.max[UserLastName.MAX])
      .matches(generalRegexp, UserValidation.regexp)
      .nullable(),
    [ProfileFields.FIRST_NAME]: Yup.string()
      .trim()
      .min(UserFirstName.MIN, Warning.min[UserFirstName.MIN])
      .max(UserFirstName.MAX, Warning.max[UserFirstName.MAX])
      .matches(generalRegexp, UserValidation.regexp)
      .nullable(),
    [ProfileFields.MIDDLE_NAME]: Yup.string()
      .trim()
      .min(UserMiddleName.MIN, Warning.min[UserMiddleName.MIN])
      .max(UserMiddleName.MAX, Warning.max[UserMiddleName.MAX])
      .matches(generalRegexp, UserValidation.regexp)
      .nullable(),
    [ProfileFields.FULL_NAME]: Yup.string()
      .trim()
      .min(UserFullName.MIN, Warning.min[UserFullName.MIN])
      .max(UserFullName.MAX, Warning.max[UserFullName.MAX])
      .matches(generalRegexp, UserValidation.regexp)
      .required(Warning.required)
      .nullable(),
    [ProfileFields.INITIALS]: Yup.string()
      .trim()
      .min(UserInitials.MIN, Warning.min[UserInitials.MIN])
      .max(UserInitials.MAX, Warning.max[UserInitials.MAX])
      .matches(generalRegexp, UserValidation.regexp)
      .nullable(),
    [ProfileFields.LOCALE]: Yup.string().required(Warning.required),
    [ProfileFields.TIME_ZONE]: Yup.string().required(Warning.required),
    [ProfileFields.POSITION]: Yup.string()
      .trim()
      .max(UserCompanyPosition.MAX, Warning.max[UserCompanyPosition.MAX])
      .matches(generalRegexp, UserValidation.regexp)
      .nullable(),
    [ProfileFields.EMAIL]: Yup.string()
      .trim()
      .nullable()
      .email(Warning.email)
      .matches(emailRegexp, Warning.email)
      .max(registerEmailLength.MAX, Warning.max[registerEmailLength.MAX]),
    [ProfileFields.ROLE]: Yup.string().required(Warning.required),
    [ProfileFields.PHONE]: Yup.string().nullable(),
  })
  .test('email-error', Warning.required, function (value) {
    const email = value?.email;
    const phoneDigits = (value?.phone ?? '').replace(/\D/g, '');
    if (email || phoneDigits) return true;

    return this.createError({
      path: 'email',
      message: Warning.requiredEmailOrPhone,
    });
  })
  .test('phone-error', Warning.required, function (value) {
    const email = value?.email;
    const phoneDigits = (value?.phone ?? '').replace(
      phoneFieldNumberRegexp,
      ''
    );

    if (phoneDigits && value.phone && !checkValidityPhoneNumber(value.phone)) {
      return this.createError({
        path: 'phone',
        message: Warning.invalidPhoneNumber,
      });
    }

    if (email || phoneDigits) return true;

    return this.createError({
      path: 'phone',
      message: Warning.requiredEmailOrPhone,
    });
  });

export const userSettingsValidationSchema = Yup.object()
  .shape({
    [ProfileFields.POSITION]: Yup.string()
      .trim()
      .max(UserCompanyPosition.MAX, Warning.max[UserCompanyPosition.MAX])
      .matches(generalRegexp, UserValidation.regexp)
      .nullable(),
  })
